/**
 * Offline interception for axios.
 *
 * THIS IS THE LOAD-BEARING IDEA OF THE WHOLE FEATURE.
 *
 * IMT has ~88 routes and ~275 components. Teaching each of them to read from a
 * local cache would be a rewrite, and every screen added afterwards would
 * silently lack offline support. Instead we intercept one layer lower: at the
 * axios instance every service already uses. A GET issued by any component, on
 * any screen, resolves from the snapshot without that component knowing
 * offline mode exists.
 *
 * Three rules, in priority order:
 *
 *  1. When online, do nothing. Not "do something cheap" — literally return the
 *     request untouched. Offline support must not be able to break the normal
 *     path.
 *  2. When offline with a live pack, serve GETs from IndexedDB. A URL that is
 *     not in the pack fails as a distinguishable error, never as an empty
 *     success: rendering an empty deal list would read as "you have no deals",
 *     which is worse than an error.
 *  3. Writes always fail fast with OFFLINE_READONLY. The user gets a real
 *     explanation instead of a 30-second hang ending in "Network Error".
 */

import { getResponse, normalizeUrl, VESSEL_POSITIONS_KEY } from './db';

export const OFFLINE_READONLY = 'OFFLINE_READONLY';
export const OFFLINE_MISS = 'OFFLINE_MISS';
export const OFFLINE_NO_PACK = 'OFFLINE_NO_PACK';

/* The context owns this; the adapter only reads it. A module-level holder (not
 * React state) because axios interceptors run outside the React tree. */
const state = {
  offline: false,
  packLive: false,
  generatedAt: null,
  onBlockedWrite: null,
  onMiss: null,
};

export function setOfflineState(next) {
  Object.assign(state, next);
}

/**
 * Every distinct URL the app asked for that the pack did not have.
 *
 * Exposed on `window` so a miss can be inspected from the console on a real
 * device without a debugger attached — the whole point of offline mode is
 * that it is used where you cannot easily instrument anything.
 */
const missedUrls = new Set();

function recordMiss(url) {
  missedUrls.add(url);
  if (typeof window !== 'undefined') {
    window.__hmsOfflineMisses = [...missedUrls];
  }
}

export function getMissedUrls() {
  return [...missedUrls];
}

/** Writes the app attempted while offline — the toast says one was blocked,
 *  this says which, which is what you need to decide if it was user-initiated. */
const blockedWrites = new Set();

function recordBlockedWrite(entry) {
  blockedWrites.add(entry);
  if (typeof window !== 'undefined') {
    window.__hmsOfflineBlockedWrites = [...blockedWrites];
  }
}

export function getOfflineState() {
  return { ...state };
}

export function isOfflineActive() {
  return state.offline && state.packLive;
}

function offlineError(code, message, config, extra = {}) {
  const error = new Error(message);
  error.isOfflineError = true;
  error.code = code;
  error.config = config;
  error.offline = { code, message, generatedAt: state.generatedAt, ...extra };
  // Shape it enough that existing `error.response?.status` handling does not
  // explode. 503 is honest: the resource is genuinely unavailable right now.
  error.response = {
    status: 503,
    statusText: 'Offline',
    data: { offline: true, code, detail: message },
    config,
    headers: {},
  };
  return error;
}

function syntheticResponse(data, config) {
  return {
    data,
    status: 200,
    statusText: 'OK (offline snapshot)',
    headers: { 'x-hms-offline': '1' },
    config,
    request: null,
    fromOfflinePack: true,
  };
}

const READ_METHODS = ['get', 'head'];

/**
 * Writes that are the app talking to itself, not a user saving something.
 *
 * These still fail offline (correctly — they cannot work), but they must not
 * raise the "that change wasn't saved" toast, because the user did not make a
 * change. `warm-llm` is fired on every mount from five different components
 * plus AuthContext, so without this the toast appears the instant you open the
 * app on a plane, every time, for something nobody asked for. `logout` is a
 * local action that completes fine offline via its own `finally` block.
 */
const SILENT_WRITE_PATHS = [
  '/api/knowledge-hub/warm-llm/',
  '/api/authentication/logout/',
  /* Sanctions screening. A POST only because it takes a list of IMOs, and
   * fired automatically by both Trade Ops dashboards on mount — so before this
   * line, opening Trade Ops offline popped "that change wasn't saved" for a
   * lookup nobody asked for. Observed 2026-08-11.
   *
   * SILENT IS NOT SERVED. This still fails with OFFLINE_MISS and must keep
   * failing: unlike fleet-positions, a compliance verdict is deliberately NOT
   * in the pack (see registry.py and UNAVAILABLE_OFFLINE — "a cached verdict
   * must never be relied on"). PREMISE: nothing below ever answers this from
   * the snapshot. If someone adds compliance data to a pack, this entry is not
   * the thing to change — that decision is in registry.py. */
  '/api/globe/vessel-compliance-batch/',
];

function isSilentWrite(url) {
  if (!url) return false;
  return SILENT_WRITE_PATHS.some((path) => url.includes(path));
}

function methodOf(config) {
  return (config.method || 'get').toLowerCase();
}

function isFleetPositionsRequest(config) {
  return !!(config.url && config.url.includes('/api/globe/fleet-positions/'));
}

/**
 * Rebuild a fleet-positions response from the cached snapshot.
 *
 * Filters to the IMOs the caller actually asked for rather than returning
 * every cached vessel — the same "don't hand back a superset of what was
 * requested" rule that the query-filter fix enforces for GETs.
 */
function buildFleetPositionsResponse(cached, config) {
  if (!cached || !Array.isArray(cached.data)) return null;

  let requested = null;
  try {
    const body = typeof config.data === 'string' ? JSON.parse(config.data) : config.data;
    if (body && Array.isArray(body.imo_list) && body.imo_list.length) {
      requested = new Set(body.imo_list.map((v) => String(v).trim()));
    }
  } catch (e) {
    requested = null;
  }

  const positions = requested
    ? cached.data.filter((p) => {
        const imo = p && (p.imo || p.imo_number || p.vessel_imo_number);
        return imo && requested.has(String(imo).trim());
      })
    : cached.data;

  const missing = requested ? Math.max(0, requested.size - positions.length) : 0;

  return {
    data: positions,
    no_position_count: missing,
    captured_at: cached.captured_at || null,
    is_cached: true,
  };
}

/**
 * Install onto an axios instance.
 *
 * Implemented as a request interceptor that short-circuits by rejecting with a
 * marker, plus a response interceptor that converts the marker into a
 * successful response. This is the standard way to fulfil a request from an
 * interceptor without swapping out the adapter wholesale — swapping the
 * adapter would also bypass the app's existing loading-spinner interceptors.
 */
export function installOfflineInterceptors(axiosInstance) {
  axiosInstance.interceptors.request.use(
    async (config) => {
      if (!isOfflineActive()) return config;

      const method = methodOf(config);

      /* Reads that are POSTs.
       *
       * `fleet-positions` is a POST only because its argument is a list of
       * IMOs too long for a query string — it mutates nothing. Treating it as
       * a write would block the vessel map offline and pop a "change wasn't
       * saved" toast for a lookup the user never initiated. Served from the
       * positions captured at pack time, filtered to the IMOs actually asked
       * for, so the caller gets exactly the shape it expects. */
      if (method === 'post' && isFleetPositionsRequest(config)) {
        const cached = await getResponse({ pathname: VESSEL_POSITIONS_KEY, search: '', full: VESSEL_POSITIONS_KEY })
          .catch(() => undefined);
        const payload = buildFleetPositionsResponse(cached, config);
        if (payload) {
          const fulfilled = new Error('offline-cache-hit');
          fulfilled.__offlineFulfilled = syntheticResponse(payload, config);
          throw fulfilled;
        }
        // No positions captured — fall through to the honest miss below.
        recordMiss('/api/globe/fleet-positions/');
        throw offlineError(
          OFFLINE_MISS,
          'Vessel positions were not saved to your offline pack.',
          config,
          { url: '/api/globe/fleet-positions/' }
        );
      }

      if (!READ_METHODS.includes(method)) {
        const error = offlineError(
          OFFLINE_READONLY,
          'You are offline. IMT is in read-only mode, so this change cannot be saved.',
          config,
          { url: config.url, method }
        );
        recordBlockedWrite(`${method.toUpperCase()} ${config.url}`);
        if (state.onBlockedWrite && !isSilentWrite(config.url)) {
          state.onBlockedWrite(error.offline);
        }
        throw error;
      }

      // Requests that explicitly opt out (the pack builder itself).
      if (config.skipOfflineCache) return config;

      const normalized = normalizeUrl(config.url, config.params);
      let data;
      try {
        data = await getResponse(normalized);
      } catch (e) {
        data = undefined;
      }

      if (data === undefined) {
        const asked = normalized ? normalized.full : config.url;
        const error = offlineError(
          OFFLINE_MISS,
          'This information was not included in your offline pack.',
          config,
          { url: asked }
        );
        recordMiss(asked);
        // A miss is the single most useful signal for tuning what the pack
        // captures — without it, "why is this screen empty offline?" is
        // unanswerable. Warn rather than error: a miss is expected for
        // anything deliberately not snapshotted.
        // eslint-disable-next-line no-console
        console.warn('[offline] not in pack:', asked);
        if (state.onMiss) state.onMiss(error.offline);
        throw error;
      }

      // Carry the payload out via the rejection path, then convert it back to
      // a resolved response in the response interceptor below.
      const fulfilled = new Error('offline-cache-hit');
      fulfilled.__offlineFulfilled = syntheticResponse(data, config);
      throw fulfilled;
    },
    (error) => Promise.reject(error)
  );

  axiosInstance.interceptors.response.use(
    (response) => response,
    (error) => {
      if (error && error.__offlineFulfilled) {
        return Promise.resolve(error.__offlineFulfilled);
      }
      return Promise.reject(error);
    }
  );
}

/**
 * True when an error came from offline mode rather than the network/server.
 * Components and error handlers can use this to show the right message.
 */
export function isOfflineError(error) {
  return !!(error && (error.isOfflineError || error?.response?.data?.offline));
}
